import React, { useState, useEffect } from "react";
import { Button, Form, Input, message, Alert, Row, Col } from "antd";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { registerAdmin, validatePassword } from "../services/authService";
import KadavraLogo from "../components/common/KadavraLogo";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f4f8f8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
  },
  logoWrap: {
    marginBottom: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  badge: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#44b07f",
    backgroundColor: "#e6f5ee",
    padding: "3px 10px",
    borderRadius: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)",
    padding: "36px 40px 32px",
    width: "100%",
    maxWidth: "520px",
  },
  heading: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#1b2d21",
    margin: "0 0 4px 0",
    lineHeight: 1.3,
  },
  subheading: {
    fontSize: "14px",
    color: "#6a6e76",
    margin: "0 0 28px 0",
  },
  label: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#3e4c5f",
  },
  submitBtn: {
    width: "100%",
    height: "44px",
    backgroundColor: "#44b07f",
    borderColor: "#44b07f",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#ffffff",
    boxShadow: "none",
  },
  signinWrap: {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "13px",
    color: "#3e4c5f",
  },
  signinLink: {
    color: "#44b07f",
    fontWeight: 600,
    textDecoration: "none",
  },
  reqList: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#8c8c8c",
  },
  footer: {
    marginTop: "32px",
    fontSize: "12px",
    color: "#a0aab4",
    textAlign: "center",
  },
};

const inputStyle = { borderRadius: "8px" };

function SignUp() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { inviteToken } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });

  useEffect(() => {
    // Both the invite token and email are required to register
    if (!inviteToken || !searchParams.get("email")) {
      setInviteError(
        "Invalid invite link. Please use the link from your invite email."
      );
      return;
    }

    // Pre-populate the email field from the invite
    form.setFieldsValue({ email: searchParams.get("email") });
  }, [inviteToken, searchParams, form]);

  const onFinish = async (values) => {
    if (!inviteToken) {
      message.error(
        "Invalid invite link. Please use the link from your invite email."
      );
      return;
    }
    setLoading(true);
    try {
      const strength = validatePassword(values.password);
      if (!strength.isValid) {
        throw new Error("Password does not meet requirements");
      }

      const decodedInviteToken = decodeURIComponent(inviteToken);

      await registerAdmin({
        email: values.email.trim(),
        password: values.password,
        phone_number: values.phone_number.trim(),
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        invite_token: decodedInviteToken,
      });

      message.success("Registration successful! You can now sign in.");
      navigate("/sign-in");
    } catch (error) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("exists")
      ) {
        message.error(
          "An account with this email already exists. Please try signing in."
        );
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Registration failed";
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordRules = [
    { required: true, message: "Please input your password!" },
    {
      validator: (_, value) => {
        const strength = validatePassword(value);
        if (!strength.isValid) {
          return Promise.reject(strength.feedback.join(", "));
        }
        return Promise.resolve();
      },
    },
  ];

  const phoneRules = [
    { required: true, message: "Please input your phone number!" },
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();
        const cleanNumber = value.replace(/[^\d+]/g, "");
        if (!/^\+\d{8,}$/.test(cleanNumber)) {
          return Promise.reject("Invalid phone number format");
        }
        return Promise.resolve();
      },
    },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.logoWrap}>
        <KadavraLogo />
        <span style={styles.badge}>Admin Portal</span>
      </div>

      <div style={styles.card}>
        <h1 style={styles.heading}>Create your admin account</h1>
        <p style={styles.subheading}>
          Complete the form below to finish setting up your account
        </p>

        {inviteError ? (
          <Alert
            message="Invalid invite"
            description={inviteError}
            type="error"
            showIcon
          />
        ) : (
          <Form
            form={form}
            name="basic"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="firstname"
                  label={<span style={styles.label}>First name</span>}
                  rules={[
                    { required: true, message: "Please input your first name!" },
                    { max: 150, message: "Maximum 150 characters" },
                  ]}
                >
                  <Input placeholder="John" size="large" style={inputStyle} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="lastname"
                  label={<span style={styles.label}>Last name</span>}
                  rules={[
                    { required: true, message: "Please input your last name!" },
                    { max: 150, message: "Maximum 150 characters" },
                  ]}
                >
                  <Input placeholder="Doe" size="large" style={inputStyle} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={<span style={styles.label}>Email</span>}
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Invalid email format" },
              ]}
            >
              <Input
                placeholder="you@kadavrabdc.com"
                size="large"
                disabled={!!inviteToken}
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="phone_number"
              label={<span style={styles.label}>Phone number</span>}
              rules={phoneRules}
            >
              <Input
                placeholder="+2341234567890"
                size="large"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={styles.label}>Password</span>}
              rules={passwordRules}
              validateTrigger={["onBlur"]}
              extra={
                <div style={styles.reqList}>
                  <p style={{ marginBottom: "4px" }}>Password must contain:</p>
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    <li>At least 8 characters</li>
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one number</li>
                    <li>At least one special character</li>
                  </ul>
                </div>
              }
            >
              <Input.Password
                placeholder="Enter a strong password"
                size="large"
                style={inputStyle}
                onChange={(e) => {
                  const val = e.target.value || "";
                  if (val.length <= 50) {
                    form.setFieldsValue({ password: val });
                    setPasswordStrength(validatePassword(val));
                  }
                }}
              />
            </Form.Item>

            {passwordStrength.score > 0 && !passwordStrength.isValid && (
              <div style={{ marginTop: "-12px", marginBottom: "16px" }}>
                <div
                  style={{
                    height: "4px",
                    background: "#f0f0f0",
                    borderRadius: "2px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      height: "100%",
                      background:
                        passwordStrength.score < 3
                          ? "#ff4d4f"
                          : passwordStrength.score < 4
                          ? "#faad14"
                          : "#52c41a",
                      transition: "all 0.3s",
                    }}
                  />
                </div>
              </div>
            )}

            <Form.Item
              name="confirm"
              label={<span style={styles.label}>Confirm password</span>}
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("The two passwords do not match!")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Re-enter your password"
                size="large"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={styles.submitBtn}
              >
                Sign Up
              </Button>
            </Form.Item>
          </Form>
        )}

        <div style={styles.signinWrap}>
          Already have an account?{" "}
          <Link to="/sign-in" style={styles.signinLink}>
            Sign in
          </Link>
        </div>
      </div>

      <p style={styles.footer}>
        © {new Date().getFullYear()} Kadavra BDC. All rights reserved.
      </p>
    </div>
  );
}

export default SignUp;
